import { useCallback, useEffect, useState } from "react";

import { useUserContext } from "src/shared/contexts";

import {
  BUILDING_TEXT_MAX_LENGTH,
  DEFAULT_CUSTOMIZATION,
  STAMP_NAME_MAX_LENGTH,
} from "./constants";
import { createRandomDefaultCustomization } from "./randomDefaults";
import {
  Accessory,
  Backdrop,
  CanvasOrientation,
  Effect,
  Feeling,
  GraphicCustomization,
  Hackpal,
  HeaderTextOption,
  SignOption,
} from "./types";
import { useFirstName } from "./useFirstName";

// Bumped when the Leave-a-message schema replaced free-form signText
const STORAGE_KEY_PREFIX = "htn2026-post-rsvp-graphic-v2";

const storageKey = (userId: number) => `${STORAGE_KEY_PREFIX}:${userId}`;

const ENUM_VALIDATORS: {
  [K in keyof GraphicCustomization]: (value: unknown) => boolean;
} = {
  orientation: (v) =>
    Object.values(CanvasOrientation).includes(v as CanvasOrientation),
  backdrop: (v) => Object.values(Backdrop).includes(v as Backdrop),
  hackpal: (v) => Object.values(Hackpal).includes(v as Hackpal),
  feeling: (v) => Object.values(Feeling).includes(v as Feeling),
  accessory: (v) => Object.values(Accessory).includes(v as Accessory),
  effect: (v) => Object.values(Effect).includes(v as Effect),
  headerText: (v) =>
    Object.values(HeaderTextOption).includes(v as HeaderTextOption),
  signOption: (v) => Object.values(SignOption).includes(v as SignOption),
  buildingText: (v) => typeof v === "string",
  stampName: (v) => typeof v === "string",
};

const loadStoredCustomization = (userId: number): GraphicCustomization => {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return DEFAULT_CUSTOMIZATION;
    const parsed = JSON.parse(raw) as Partial<GraphicCustomization>;
    const result = { ...DEFAULT_CUSTOMIZATION };
    (Object.keys(ENUM_VALIDATORS) as (keyof GraphicCustomization)[]).forEach(
      (key) => {
        const value = parsed[key];
        if (ENUM_VALIDATORS[key](value)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (result as any)[key] = value;
        }
      }
    );
    result.buildingText = result.buildingText.slice(
      0,
      BUILDING_TEXT_MAX_LENGTH
    );
    result.stampName = result.stampName.slice(0, STAMP_NAME_MAX_LENGTH);
    return result;
  } catch {
    return DEFAULT_CUSTOMIZATION;
  }
};

export const useGraphicCustomization = () => {
  const { id } = useUserContext();
  const firstName = useFirstName();
  const [stored, setStored] = useState<{
    userId: number | null;
    customization: GraphicCustomization;
  }>({ userId: null, customization: DEFAULT_CUSTOMIZATION });

  useEffect(() => {
    if (id == null || stored.userId === id) return;

    if (localStorage.getItem(storageKey(id)) !== null) {
      setStored({ userId: id, customization: loadStoredCustomization(id) });
      return;
    }

    if (!firstName) return;

    setStored({
      userId: id,
      customization: {
        ...createRandomDefaultCustomization(),
        stampName: firstName.slice(0, STAMP_NAME_MAX_LENGTH),
      },
    });
  }, [id, firstName, stored.userId]);

  useEffect(() => {
    if (stored.userId == null || stored.userId !== id) return;
    try {
      localStorage.setItem(
        storageKey(stored.userId),
        JSON.stringify(stored.customization)
      );
    } catch {
      // storage full / unavailable — customization just won't persist
    }
  }, [stored, id]);

  const setOption = useCallback(
    <K extends keyof GraphicCustomization>(
      key: K,
      value: GraphicCustomization[K]
    ) => {
      setStored((prev) => ({
        ...prev,
        customization: { ...prev.customization, [key]: value },
      }));
    },
    []
  );

  return { customization: stored.customization, setOption };
};
